import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLike } from '@apollo/client';
import _get from 'lodash/get';
import {
  AiDocumentationFieldEnum,
  Documentation,
  useEdmDocumentationUpdateMutation,
  useTagCreateMutation,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { AiFieldGenerationSource } from 'routes/Documentation/DocumentForm/utils/useAiFieldGeneration';
import getAiFieldDetectionState, {
  AiFieldUiState,
} from 'routes/Documentation/utils/getAiFieldDetectionState';
import useAiDetectionDismiss from 'routes/Documentation/utils/useAiDetectionDismiss';

type FieldAssistView = {
  uiState: AiFieldUiState;
  // Fed into AiFieldAssist's resolveSource prop — always the saved version,
  // since every consumer of this hook already has a persisted document.
  resolveSource: () => AiFieldGenerationSource | null;
  onDismiss: () => void;
};

export type DocumentFieldDetection = {
  name: FieldAssistView & { onApplyValue: (value: string) => void };
  description: FieldAssistView & { onApplyValue: (value: string) => void };
  category: FieldAssistView & {
    onApplyCategoryId: (categoryId: string) => void;
  };
  tags: FieldAssistView & { onApplyTagNames: (names: string[]) => void };
  code: FieldAssistView & {
    suggestedCodes: string[];
    onApplyValue: (value: string) => void;
    // A manual Save (or an auto-applied suggestion pick) is being persisted.
    isSaving: boolean;
  };
};

type Args = {
  doc:
    | Pick<
        Documentation,
        | 'id'
        | 'name'
        | 'code'
        | 'description'
        | 'tags'
        | 'category'
        | 'aiDetectionStatus'
        | 'suggestedCodes'
      >
    | null
    | undefined;
  // The saved version whose file regeneration reads from.
  documentationVersionId: string | undefined;
  // Whether AI detection is available (company has the AI plan). Threaded
  // into the update mutation so its response includes fresh
  // aiDetectionStatus/suggestedCodes (edmDocumentationAiDetectionFragment),
  // keeping the Apollo cache correct the instant it resolves.
  isAiEnabled: boolean;
};

const NAME = AiDocumentationFieldEnum.Name;
const DESCRIPTION = AiDocumentationFieldEnum.Description;
const CATEGORY = AiDocumentationFieldEnum.Category;
const TAGS = AiDocumentationFieldEnum.Tags;
const CODE = AiDocumentationFieldEnum.Code;

// Drives the persisted AI-detection status (Detecting/Failed/MultipleCodes)
// for a single document's NAME/DESCRIPTION/CATEGORY/TAGS/CODE fields, for use
// with the shared AiFieldAssist component (Task 1) on the read-only List and
// View surfaces. AiFieldAssist owns its own live generation state internally
// (exactly like the form) — this hook only supplies the persisted uiState,
// the generation source, the BE dismiss action, and how a completed
// generation (or, for CODE, a manually-typed override) gets persisted: the
// ordinary documentation update mutation. Called once in DocumentViewPage per
// document, and independently by each of the four List cells per row (so a
// row with all four AI-aware columns visible mounts four instances).
export default function useDocumentFieldDetection({
  doc,
  documentationVersionId,
  isAiEnabled,
}: Args): DocumentFieldDetection {
  const { t } = useTranslation();
  const [updateMutation] = useEdmDocumentationUpdateMutation();
  const [tagCreateMutation] = useTagCreateMutation();
  const docId = doc?.id;
  const { isDismissed, dismiss: dismissDetection } =
    useAiDetectionDismiss(docId);
  const [savingFields, setSavingFields] = useState<
    Set<AiDocumentationFieldEnum>
  >(new Set());

  const markSaving = useCallback((field: AiDocumentationFieldEnum) => {
    setSavingFields((prev) => new Set(prev).add(field));
  }, []);
  const clearSaving = useCallback((field: AiDocumentationFieldEnum) => {
    setSavingFields((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  }, []);

  const persist = useCallback(
    (
      field: AiDocumentationFieldEnum,
      attributes: {
        name?: string;
        description?: string;
        categoryId?: string;
        documentationTags?: { id: string }[];
        code?: string;
      },
    ) => {
      if (!docId) return;
      markSaving(field);
      updateMutation({
        variables: {
          id: docId,
          attributes,
          isAiDetectionEnabled: isAiEnabled,
        },
        onCompleted: () => {
          toast({ content: t('update-document-success') });
          clearSaving(field);
        },
        onError: (error) => {
          showGraphQLErrors(error, t);
          clearSaving(field);
        },
      });
    },
    [clearSaving, docId, isAiEnabled, markSaving, t, updateMutation],
  );

  // Resolve one generated tag name to a tag id, creating it or reusing the
  // existing one when the name is already taken (mirrors TagsInput behaviour).
  // Only the `taken` duplicate is recovered from; every other failure
  // (network / 5xx / permission / other validation) is re-thrown so
  // onApplyTagNames can surface it rather than silently dropping the tag.
  const resolveTagId = useCallback(
    async (name: string): Promise<string> => {
      try {
        const res = await tagCreateMutation({
          variables: { attributes: { name } },
        });
        const id = _get(res, 'data.data.id', null) as string | null;
        // A mutation that resolves without an id and without throwing is
        // unexpected — treat it as a failure instead of a silent no-op.
        if (!id) throw new Error(`tagCreate returned no id for "${name}"`);
        return id;
      } catch (error) {
        const meta = _get(
          error,
          ['graphQLErrors', 0, 'extensions', 'details', 0, 'meta', 'tag'],
          {},
        ) as { id?: string; reason?: string };
        if (meta.reason === 'taken' && meta.id) return meta.id;
        throw error;
      }
    },
    [tagCreateMutation],
  );

  const onApplyTagNames = useCallback(
    async (names: string[]) => {
      markSaving(TAGS);
      try {
        const ids = await Promise.all(names.map(resolveTagId));
        if (!ids.length) {
          clearSaving(TAGS);
          return;
        }
        persist(TAGS, { documentationTags: ids.map((id) => ({ id })) });
      } catch (error) {
        // A real tag-resolution failure must not leave the apply as a silent
        // no-op: surface it and clear the saving state.
        showGraphQLErrors(error as ErrorLike, t);
        clearSaving(TAGS);
      }
    },
    [clearSaving, markSaving, persist, resolveTagId, t],
  );

  const resolveSource = useCallback(
    (): AiFieldGenerationSource | null =>
      documentationVersionId ? { documentationVersionId } : null,
    [documentationVersionId],
  );

  const fieldView = (field: AiDocumentationFieldEnum): FieldAssistView => ({
    uiState: getAiFieldDetectionState({
      doc,
      field,
      isDismissed: isDismissed(field),
    }).uiState,
    resolveSource,
    onDismiss: () => dismissDetection(field),
  });

  return {
    name: {
      ...fieldView(NAME),
      onApplyValue: (value: string) => persist(NAME, { name: value }),
    },
    description: {
      ...fieldView(DESCRIPTION),
      onApplyValue: (value: string) =>
        persist(DESCRIPTION, { description: value }),
    },
    category: {
      ...fieldView(CATEGORY),
      onApplyCategoryId: (categoryId: string) =>
        persist(CATEGORY, { categoryId }),
    },
    tags: {
      ...fieldView(TAGS),
      onApplyTagNames,
    },
    code: {
      ...fieldView(CODE),
      suggestedCodes: doc?.suggestedCodes ?? [],
      onApplyValue: (value: string) => persist(CODE, { code: value }),
      isSaving: savingFields.has(CODE),
    },
  };
}
